import { getCurrentPosition } from "@/common/hooks/getCurrentPosition";
import dayjs from "dayjs";
import { useState } from "react";

export function makeGeolocationPositionSerializable(
	position: GeolocationPosition | null | undefined,
) {
	return position
		? {
				coords: {
					latitude: position.coords?.latitude || null,
					longitude: position.coords?.longitude || null,
					altitude: position.coords?.altitude || null,
					accuracy: position.coords?.accuracy || null,
					altitudeAccuracy: position.coords?.altitudeAccuracy || null,
					heading: position.coords?.heading || null,
					speed: position.coords?.speed || null,
				},
				timestamp: position.timestamp || null,
			}
		: null;
}

export function useGetGpsLocation() {
	const [isGpsLoading, setIsGpsLoading] = useState(false);
	const [gpsLocation, setGpsLocation] = useState<GeolocationPosition | null>(
		null,
	);
	const getGpsLocation = async () => {
		setIsGpsLoading(true);
		try {
			const position = await Promise.race([
				getCurrentPosition({
					timeout: 60000,
					maximumAge: 5000, // 5 seconds
					enableHighAccuracy: true,
				}).catch((e) => {
					// if position unavailable try higher max age
					if (e.code === 2) {
						return getCurrentPosition({
							timeout: 60000,
							maximumAge: 5000 * 60, // 5 minutes
							enableHighAccuracy: true,
						});
					}

					throw e;
				}),
				// in case the timeout is not respected
				// throw a timeout error if above takes longer than combined timeout + buffer
				new Promise<GeolocationPosition>((_, reject) =>
					setTimeout(() => reject(new Error("GPS location timeout")), 130_000),
				),
			]);

			const isStale = position.timestamp
				? dayjs(position.timestamp).isBefore(dayjs().subtract(5, "minute"))
				: false;
			if (isStale) {
				throw new Error("GPS location is stale");
			}

			setGpsLocation(position);
			return position;
		} finally {
			setIsGpsLoading(false);
		}
	};

	return {
		isGpsLoading,
		gpsLocation,
		getGpsLocation,
	};
}
